import { useState } from 'react';
import { Moon, Sun, Menu, X, Scissors, MapPin, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Header = ({ theme, toggleTheme, onBecomeBarber, onSignIn, user }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState('Lagos');

  const cities = ['Lagos', 'Abuja', 'Ibadan', 'Port Harcourt', 'Kano', 'Enugu', 'Benin City'];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header data-testid="header" className="sticky top-0 z-50 bg-black/60 dark:bg-black/60 backdrop-blur-xl border-b border-white/10">
      <div className="container mx-auto">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div data-testid="logo" className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="relative">
              <Scissors className="w-6 h-6 md:w-7 md:h-7 text-[#00C167]" />
              <MapPin className="w-3 h-3 text-[#00C167] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-xl md:text-2xl font-black tracking-tight text-white">BarberGo</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <button onClick={() => scrollToSection('hero')} className="text-gray-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-book">
              Book a Barber
            </button>
            <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-services">
              Services
            </button>
            <button onClick={() => scrollToSection('for-barbers')} className="text-gray-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-barbers">
              For Barbers
            </button>
            <button onClick={() => scrollToSection('how-it-works')} className="text-gray-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-how">
              How it Works
            </button>
            <button onClick={() => scrollToSection('cities')} className="text-gray-300 hover:text-white transition-colors text-sm font-medium" data-testid="nav-cities">
              Cities
            </button>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3 md:gap-4">
            {/* City Selector - Desktop */}
            <div className="hidden md:block" data-testid="city-selector">
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger className="w-[130px] bg-[#1A1A1A] border-white/10 text-white focus:border-[#00C167] focus:ring-[#00C167]/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
                  {cities.map(city => (
                    <SelectItem key={city} value={city} className="focus:bg-[#00C167]/20 focus:text-white">
                      {city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-gray-300 hover:text-white hover:bg-white/10"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {/* Sign In / User Profile */}
            {user ? (
              <div 
                onClick={() => navigate('/customer-dashboard')}
                className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/20 cursor-pointer hover:bg-white/20 transition-all"
                data-testid="user-profile-button"
              >
                <div className="w-8 h-8 rounded-full bg-[#00C167] flex items-center justify-center text-white font-bold text-sm">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <span className="text-white text-sm font-medium">{user.name}</span>
                <User className="w-4 h-4 text-white/60" />
              </div>
            ) : (
              <Button
                variant="ghost"
                onClick={onSignIn}
                className="hidden md:inline-flex text-gray-300 hover:text-white hover:bg-white/10"
                data-testid="sign-in-button"
              >
                Sign in
              </Button>
            )}

            {/* Download App Button - Desktop */}
            <Button
              onClick={() => scrollToSection('app-download')}
              className="hidden lg:inline-flex bg-[#00C167] hover:bg-[#009E54] text-white"
              data-testid="download-app-button"
            >
              Download App
            </Button>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-gray-300 hover:text-white hover:bg-white/10"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden py-4 border-t border-white/10">
            <nav className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('hero')} className="text-gray-300 hover:text-white transition-colors text-left py-2" data-testid="mobile-nav-book">
                Book a Barber
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-white transition-colors text-left py-2" data-testid="mobile-nav-services">
                Services
              </button>
              <button onClick={() => scrollToSection('for-barbers')} className="text-gray-300 hover:text-white transition-colors text-left py-2" data-testid="mobile-nav-barbers">
                For Barbers
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-gray-300 hover:text-white transition-colors text-left py-2" data-testid="mobile-nav-how">
                How it Works
              </button>
              <button onClick={() => scrollToSection('cities')} className="text-gray-300 hover:text-white transition-colors text-left py-2" data-testid="mobile-nav-cities">
                Cities
              </button>
              <div className="pt-2 border-t border-white/10">
                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger className="w-full bg-[#1A1A1A] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
                    {cities.map(city => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full bg-[#00C167] hover:bg-[#009E54] text-white" data-testid="mobile-download-app">
                Download App
              </Button>
              <Button variant="outline" className="w-full" data-testid="mobile-sign-in">
                Sign in
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;